import { SecureRoute } from '@okta/okta-react';
import React from 'react';
import { Redirect, Switch, useParams } from 'react-router';
import { SalesforceConfig } from 'api/integrations';
import { PageLoading } from 'components/atoms/loading';
import { NotFound } from 'components/atoms/result';
import { IntegrationStatusTypes } from 'components/organisms/module-settings/integrations/integrations-list/authentication-types';
import { AutomationList } from 'components/organisms/module-settings/integrations/integrations-list/salesforce/automation/list/list';
import { ResourceMappings } from 'components/organisms/module-settings/integrations/integrations-list/salesforce/resource-mappings/resource-mappings';
import { SalesforceLayout } from 'components/organisms/module-settings/integrations/integrations-list/salesforce/salesforce-layout';
import { SalesforceSettings } from 'components/organisms/module-settings/integrations/integrations-list/salesforce/settings/settings';
import { RoutePaths } from 'constant';
import {
  useSalesforceConfigByIdQuery,
  useSalesforceConfigQuery,
} from 'queries/salesforce-integration';
import { AutomationsCreatePage } from './automation-create-page';
import { AutomationsEditPage } from './automation-edit-page';

const getConfig = (data?: SalesforceConfig[]) => data?.[0];

// TODO: @krishna add helpers to generate the URLs
const AutomaticRedirection = () => {
  const { data, status } = useSalesforceConfigQuery();

  switch (status) {
    case 'loading': {
      return <PageLoading />;
    }
    case 'success': {
      const config = getConfig(data);

      if (config) {
        if (config.integrationStatus === IntegrationStatusTypes.Expired) {
          return (
            <Redirect
              push
              to={`${RoutePaths.salesforceIntegrations}/${config.integrationId}/settings`}
            />
          );
        }

        return (
          <Redirect
            push
            to={`${RoutePaths.salesforceIntegrations}/${config.integrationId}/automations`}
          />
        );
      }

      return (
        <Redirect
          push
          to={`${RoutePaths.salesforceIntegrations}/new/settings`}
        />
      );
    }
    case 'error': {
      return <Redirect push to={RoutePaths.salesforceIntegrations} />;
    }
    default: {
      return null;
    }
  }
};

const SalesforceAutomationLandingPage = () => {
  const { integrationId } = useParams<{ integrationId: string }>();
  const { data } = useSalesforceConfigByIdQuery(Number(integrationId), {
    enabled: integrationId !== 'new',
  });

  if (data?.integrationStatus === IntegrationStatusTypes.Expired) {
    return <AutomaticRedirection />;
  }

  return (
    <Switch>
      <SecureRoute path={RoutePaths.salesforceAutomationsNew}>
        <AutomationsCreatePage />
      </SecureRoute>

      <SecureRoute path={RoutePaths.salesforceAutomationsEdit}>
        <AutomationsEditPage />
      </SecureRoute>

      <SecureRoute path={RoutePaths.salesforceAutomations}>
        <SalesforceLayout>
          <AutomationList />
        </SalesforceLayout>
      </SecureRoute>

      <SecureRoute path="*">
        <NotFound />
      </SecureRoute>
    </Switch>
  );
};

// TODO: @krishna split the settings auth/disconnect into two independent routes
const SalesForceIntegrationsByIdPage = () => {
  const { integrationId } = useParams<{ integrationId: string }>();
  const { isError, data, isLoading } = useSalesforceConfigByIdQuery(
    Number(integrationId),
    { enabled: integrationId !== 'new' },
  );

  if (isLoading) {
    return <PageLoading />;
  }

  if (isError) {
    return <AutomaticRedirection />;
  }

  return (
    <Switch>
      <SecureRoute path={RoutePaths.salesforceAutomations}>
        <SalesforceAutomationLandingPage />
      </SecureRoute>

      <SecureRoute path={RoutePaths.salesforceResourceMapping}>
        <ResourceMappings />
      </SecureRoute>

      <SecureRoute path={RoutePaths.salesforceSettings}>
        <SalesforceSettings data={data} />
      </SecureRoute>

      <SecureRoute path="*">
        <NotFound />
      </SecureRoute>
    </Switch>
  );
};

export const SalesForceIntegrationsLandingPage = () => {
  return (
    <Switch>
      <SecureRoute path={RoutePaths.salesforceIntegrationById}>
        <SalesForceIntegrationsByIdPage />
      </SecureRoute>

      <SecureRoute path={RoutePaths.salesforceIntegrations}>
        <AutomaticRedirection />
      </SecureRoute>

      <SecureRoute path="*">
        <NotFound />
      </SecureRoute>
    </Switch>
  );
};
